// src/components/ErrorFallback.tsx
import { FallbackProps } from 'react-error-boundary';
import { useEffect } from 'react';

function isChunkLoadError(error: Error): boolean {
  const msg = error?.message || "";
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Failed to fetch") ||
    msg.includes("Loading chunk") ||
    msg.includes("Loading CSS chunk") ||
    (error?.name === "TypeError" && msg.includes("fetch"))
  );
}

export function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  useEffect(() => {
    if (isChunkLoadError(error)) {
      const reloadKey = "error-boundary-chunk-reload";
      const lastReload = sessionStorage.getItem(reloadKey);
      const now = Date.now();

      // Auto-reload once to get fresh assets; prevent infinite loop with 10s guard
      if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
        sessionStorage.setItem(reloadKey, now.toString());
        window.location.reload();
        return;
      }
    }
  }, [error]);

  // You can also log the error to a service like Sentry or LogRocket here
  console.error(error);

  const isChunkError = isChunkLoadError(error);

  return (
   <div
      className="flex flex-col items-center justify-center min-h-screen bg-gray-100"
      role="alert"
    >
      <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800">
            {isChunkError ? "Application Updated" : "Something went wrong"}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {isChunkError
              ? "A new version of the application is available. Please reload the page to continue."
              : "We're sorry, but an unexpected error has occurred."}
          </p>
        </div>
        {!isChunkError && (
          <div className="p-4 bg-red-100 border border-red-400 rounded-md">
            <pre className="text-sm text-red-700 whitespace-pre-wrap">
              {error.message}
            </pre>
          </div>
        )}
        <button
          onClick={() => {
            if (isChunkError) {
              window.location.reload();
            } else {
              resetErrorBoundary();
            }
          }}
          className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          {isChunkError ? "Reload Page" : "Try again"}
        </button>
      </div>
    </div>
  );
}